// Soft-bundle guarantee labels: mirrors the markup/CSS classes of
// snippets/empco-nested-guarantee-label.liquid, but driven by a client-side Storefront API fetch
// instead of Liquid, because Shopify does not expose fixed-bundle components to Liquid at all
// (only via Storefront/Admin GraphQL). Renders nothing when the variant isn't a bundle.
const API_VERSION = '2026-07';

const BUNDLE_COMPONENTS_QUERY = `
  query BundleComponents($variantId: ID!, $language: LanguageCode!) @inContext(language: $language) {
    node(id: $variantId) {
      ... on ProductVariant {
        components(first: 20) {
          nodes {
            productVariant {
              id
              product {
                title
              }
              garanGuarantee: metafield(namespace: "custom", key: "ATTR_garan_guarantee") {
                references(first: 10) {
                  nodes {
                    ... on Metaobject {
                      handle
                      field(key: "guarantee_asset_img") {
                        references(first: 5) {
                          nodes {
                            ... on MediaImage {
                              image { url altText }
                            }
                          }
                        }
                      }
                    }
                  }
                }
              }
              pimNestedGuarantee: metafield(namespace: "custom", key: "ATTR_garan_nested_guarantee") {
                references(first: 10) {
                  nodes {
                    ... on Metaobject {
                      handle
                      field(key: "guarantee_asset_img") {
                        references(first: 5) {
                          nodes {
                            ... on MediaImage {
                              image { url altText }
                            }
                          }
                        }
                      }
                    }
                  }
                }
              }
            }
          }
        }
      }
    }
  }
`;

function escapeHtml(value) {
  return String(value ?? '').replace(/[&<>"']/g, (ch) => ({
    '&': '&amp;',
    '<': '&lt;',
    '>': '&gt;',
    '"': '&quot;',
    "'": '&#39;',
  })[ch]);
}

function idFromGid(gid) {
  return String(gid ?? '').split('/').pop();
}

function sizedImageUrl(url, width) {
  if (!url) return url;
  const sized = new URL(url);
  sized.searchParams.set('width', String(width));
  return sized.toString();
}

function imagesFromReferences(references, width) {
  return (references?.nodes ?? [])
    .map((node) => node.image)
    .filter(Boolean)
    .map((img) => `<img src="${escapeHtml(sizedImageUrl(img.url, width))}" loading="lazy" alt="${escapeHtml(img.altText)}">`)
    .join('');
}

// One "group" per bundle component whose variant carries both guarantee metafields —
// the exact same non-blank gate the single-variant snippet applies, just per component here.
function buildComponentGroups(nodes) {
  const groups = [];
  for (const node of nodes) {
    const pv = node.productVariant;
    if (!pv) continue;
    const bigNodes = pv.garanGuarantee?.references?.nodes ?? [];
    const smallNodes = pv.pimNestedGuarantee?.references?.nodes ?? [];
    if (bigNodes.length === 0 || smallNodes.length === 0) continue;
    groups.push({
      componentVariantId: idFromGid(pv.id),
      productTitle: pv.product?.title ?? '',
      bigNodes,
      smallNodes,
    });
  }
  return groups;
}

function renderBadgeRow(groups, context, strings) {
  return groups
    .map((group) =>
      group.bigNodes
        .map((big, pairIndex) => {
          const small = group.smallNodes[pairIndex];
          const drawerId = `empco-${context}-${group.componentVariantId}-${big.handle}`;
          const ariaLabel = strings.ariaFor.replace('__PRODUCT_TITLE__', group.productTitle);
          return `
            <button
              type="button"
              class="empco-label__trigger"
              aria-controls="${drawerId}"
              aria-expanded="false"
              aria-label="${escapeHtml(ariaLabel)}"
            >${imagesFromReferences(small.field?.references, 480)}</button>
            <x-drawer id="${drawerId}" class="drawer drawer--lg">
              <div class="empco-drawer__header" slot="header">
                <span class="h5">${escapeHtml(strings.warrantyTitle)}</span>
                ${strings.garanLinkHtml}
              </div>
              <div class="prose empco-detail-list">${imagesFromReferences(big.field?.references, 1600)}</div>
            </x-drawer>
          `;
        })
        .join('')
    )
    .join('');
}

function renderAllDrawer(groups, context, productId, strings) {
  if (groups.length === 0) return '';
  const allDrawerId = `empco-all-${context}-${productId}`;
  const detailGroups = groups
    .map((group) => {
      const perLabelBlocks = group.bigNodes
        .map((big) => `<div class="empco-detail">${imagesFromReferences(big.field?.references, 1600)}</div>`)
        .join('');
      return `<p class="text-sm text-subdued">${escapeHtml(group.productTitle)}</p>${perLabelBlocks}`;
    })
    .join('');

  return `
    <button
      type="button"
      class="empco-label__all-trigger link text-sm text-subdued"
      aria-controls="${allDrawerId}"
      aria-expanded="false"
    >${escapeHtml(strings.detailsText)}</button>
    <x-drawer id="${allDrawerId}" class="drawer drawer--lg">
      <div class="empco-drawer__header" slot="header">
        <span class="h5">${escapeHtml(strings.warrantiesTitle)}</span>
        ${strings.garanLinkHtml}
      </div>
      <div class="prose empco-detail-list">${detailGroups}</div>
    </x-drawer>
  `;
}

class EmpcoBundleGuaranteeLabels extends HTMLElement {
  connectedCallback() {
    this.detectBundle();
  }

  async detectBundle() {
    const variantId = this.getAttribute('variant-id');
    const token = this.dataset.sfToken;
    if (!variantId || !token) return;

    const gid = `gid://shopify/ProductVariant/${variantId}`;
    const language = (document.documentElement.lang || 'en').split('-')[0].toUpperCase();

    try {
      const response = await fetch(`${window.location.origin}/api/${API_VERSION}/graphql.json`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Shopify-Storefront-Access-Token': token,
        },
        body: JSON.stringify({
          query: BUNDLE_COMPONENTS_QUERY,
          variables: { variantId: gid, language },
        }),
      });
      const json = await response.json();

      if (json.errors) {
        console.warn('[empco-bundle-guarantee-labels] GraphQL errors:', json.errors);
        return;
      }

      const nodes = json.data?.node?.components?.nodes ?? [];
      const groups = buildComponentGroups(nodes);
      if (groups.length === 0) return;

      const strings = {
        warrantyTitle: this.dataset.tWarrantyTitle,
        warrantiesTitle: this.dataset.tWarrantiesTitle,
        detailsText: this.dataset.tDetails,
        ariaFor: this.dataset.tAriaFor,
        garanLinkHtml: `<a href="${escapeHtml(this.dataset.garanUrl)}" class="link text-md" target="_blank" rel="noopener">${escapeHtml(this.dataset.tGaranLink)}</a>`,
      };
      const productId = this.getAttribute('product-id');

      this.innerHTML = `
        <div class="product-info__empco-nested-label">
          <div class="empco-label__row">${renderBadgeRow(groups, this.getAttribute('context'), strings)}</div>
          <div class="product-info__empco-action">${renderAllDrawer(groups, this.getAttribute('context'), productId, strings)}</div>
        </div>
      `;
      this.removeAttribute('hidden');
    } catch (err) {
      console.warn('[empco-bundle-guarantee-labels] fetch failed:', err);
    }
  }
}

if (!customElements.get('empco-bundle-guarantee-labels')) {
  customElements.define('empco-bundle-guarantee-labels', EmpcoBundleGuaranteeLabels);
}
